import { useCallback, useEffect, useRef } from 'react';
import * as R from 'remeda';
import { useDraggable } from '@dnd-kit/core';
import { v4 as uuidv4 } from 'uuid';

type UseDragPositionProps = {
  initial?: number;
  min?: number;
  max?: number;
  storageKey?: string;
  direction?: 'horizontal' | 'vertical';
};

type DragPositionValues = {
  position: number;
  containerRef: React.RefObject<HTMLDivElement>;
} & ReturnType<typeof useDraggable>;

export const useDragPosition = ({
  initial = 0,
  min = 0,
  max = Infinity,
  storageKey,
  direction = 'vertical',
}: UseDragPositionProps): DragPositionValues => {
  const savedPosition = storageKey ? localStorage.getItem(storageKey) : null;

  const defaultInitialPosition = R.clamp(initial, { min, max });

  const initialPosition = savedPosition
    ? parseInt(savedPosition, 10) || defaultInitialPosition
    : defaultInitialPosition;

  const positionRef = useRef(initialPosition);
  const containerRef = useRef<HTMLDivElement>(null);

  const dragPositionId = useRef(`drag-position-${uuidv4()}`);

  const draggableProps = useDraggable({
    id: dragPositionId.current,
    data: { position: positionRef.current },
  });

  const onPointerMove = useCallback(
    (e: MouseEvent) => {
      if (draggableProps.isDragging && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const clientPos =
          direction === 'vertical'
            ? e.clientX - rect.left
            : e.clientY - rect.top;
        positionRef.current = R.clamp(clientPos, { min, max });
      }
    },
    [draggableProps.isDragging, max, min, direction]
  );

  useEffect(() => {
    document.addEventListener('pointermove', onPointerMove);
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
    };
  }, [onPointerMove]);

  return {
    position: positionRef.current,
    containerRef,
    ...draggableProps,
  };
};
